<template>
    <div class="p-2">

        <div class="row">
            <div class="col-md-8 reporting-db-section mt-2" v-if="ticketMovementOptions.xaxis.categories">
                <h2 class="text-center"
                    style="font-weight: 700 !important; font-size: 2.2em !important; margin-bottom: 20px !important;">Ticket Movement Statistics</h2>
                <apexchart height="300" type="area" :options="ticketMovementOptions" :series="ticketMovementSeries"></apexchart>
            </div>
            <div class="col-md-4 reporting-db-section mt-2">
                <h2 class="text-center" style="font-weight: 700 !important; font-size: 2.2em !important; margin-bottom: 20px !important;">Ticket Priorities</h2>
                <apexchart height="280" type="donut" :options="ticketPriorityOptions" :series="ticketPrioritySeries"></apexchart>
            </div>
        </div>

        <!-- Stat Widget Blocks -->
        <div class="row mt-12 mb-3">
            <!-- Column -->
            <div class="col-md-6 col-lg-3 col-xlg-3">
                <div class="card card-inverse card-info">
                    <div class="box bg-info text-center">
                        <h6 class="text-white mt-2 mb-2 text-base">{{ stats.inspections.last_month_count }} Inspections Last Month</h6>
                        <h1 class="font-normal text-white mb-1 text-4xl">

                            <i class="mdi mdi-chevron-up" v-if="stats.inspections.difference_percentage > 0"></i>
                            <i class="mdi mdi-chevron-down" v-if="stats.inspections.difference_percentage < 0"></i>

                            <template v-if="stats.inspections.difference_percentage != 0">
                                {{ stats.inspections.difference_percentage }} %
                            </template>
                            <template v-else>
                                Up from 0
                            </template>

                        </h1>
                    </div>
                </div>
            </div>

            <!-- Column -->
            <div class="col-md-6 col-lg-3 col-xlg-3">
                <div class="card card-inverse card-primary">
                    <div class="box text-center" style="background-color: #f37874 !important;">
                        <h6 class="text-white mt-2 mb-2 text-base">{{ stats.tickets.last_month_count }} Tickets Last Month</h6>
                        <h1 class="font-normal text-white mb-1 text-4xl">

                            <i class="mdi mdi-chevron-up" v-if="stats.tickets.difference_percentage > 0"></i>
                            <i class="mdi mdi-chevron-down" v-if="stats.tickets.difference_percentage < 0"></i>


                            <template v-if="stats.tickets.difference_percentage != 0">
                                {{ stats.tickets.difference_percentage }} %
                            </template>
                            <template v-else>
                                Up from 0
                            </template>

                        </h1>
                    </div>
                </div>
            </div>
            <!-- Column -->

            <div class="col-md-6 col-lg-3 col-xlg-3">
                <div class="card card-inverse card-success">
                    <div class="box text-center">
                        <h6 class="text-white mt-2 mb-2 text-base">Ticketless Properties</h6>
                        <h1 class="font-normal text-white mb-1 text-4xl">
                            {{ stats.tickets.ticket_less_properties_percentage }} %
                        </h1>
                    </div>
                </div>
            </div>

            <!-- Column -->
            <div class="col-md-6 col-lg-3 col-xlg-3">
                <div class="card card-inverse card-warning">
                    <div class="box text-center">
                        <h6 class="text-white mt-2 mb-2 text-base">Inspectionless Properties</h6>
                        <h1 class="font-normal text-white mb-1 text-4xl">
                            {{ stats.inspections.inspection_less_properties_percentage }} %
                        </h1>
                    </div>
                </div>
            </div>

        </div>

        <!-- Ticket Risks Table -->
        <div class="card mb-12">
            <ticket-risk-cases :settings="stats.settings"></ticket-risk-cases>
        </div>

        <!-- User Tickets Table -->
        <div class="card mt-12 mb-12 reporting-db-section">
            <div class="card-body">
                <h2 class="text-left"
                    style="font-weight: 700 !important; font-size: 2.2em !important">User Ticket Stats</h2>
                <h6 class="card-subtitle mt-1">This table shows the ticket counts for each user with tickets assigned.</h6>
            </div>
            <div class="table-responsive">
                <table class="table table-hover no-wrap">
                    <thead>
                        <tr>
                            <th>USER</th>
                            <th class="text-center">OPEN</th>
                            <th class="text-center">UNRESOLVED</th>
                            <th class="text-center">RESOLVED <span style="font-size: 10px">(Last 7 Days)</span></th>
                            <th class="text-right">AVG RES TIME</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr class="reporting-db-normalize-row" v-for="user in stats.user_tickets">
                            <td>
                                <a :href="'/tickets?user_filter=' + user.user_id" target="_blank">{{ user.user_name }}</a>
                            </td>
                            <td class="text-center">
                                <a class="pl-3 pr-3" :href="'/tickets?user_filter=' + user.user_id + '&status_filter=open'" target="_blank">
                                    {{ user.tickets_open }}
                                </a>
                            </td>
                            <td class="text-center">
                                <a class="pl-3 pr-3"
                                   :href="'/tickets?status_filter=all&resolved_filter=false&user_id=' + user.user_id"
                                   target="_blank">
                                    {{ user.tickets_unresolved }}
                                </a>
                            </td>
                            <td class="text-center"> {{ user.tickets_resolved_last_7_days }}</td>
                            <td class="text-right"> {{ user.avg_res_time_last_30_days }}<span
                                style="font-size: 10px"> days</span></td>
                        </tr>
                    </tbody>
                </table>
            </div>
        </div>

        <!-- Maintenance Request Stats Table-->
        <div class="card mt-12 mb-12 reporting-db-section" v-if="hasFeature('feat_maintenance_requests') && stats.maintenance_requests">
            <div class="card-body">
                <h2 class="text-left"
                    style="font-weight: 700 !important; font-size: 2.2em !important">Maintenance Request Stats</h2>
                <h6 class="card-subtitle mt-1">This table shows the maintenance request counts.</h6>
            </div>
            <div class="table-responsive">
                <table class="table table-hover no-wrap">
                    <thead>
                        <tr>
                            <th>TOTAL</th>
                            <th>OPEN</th>
                            <th>CLOSED</th>
                            <th>TOTAL WITHOUT TICKETS</th>
                            <th>OPEN WITHOUT TICKETS</th>
                            <th>CLOSED WITHOUT TICKETS</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr class="reporting-db-normalize-row">
                            <td>
                                <a :href="'/maintenance/requests'" target="_blank">
                                    {{ stats.maintenance_requests.total }}
                                </a>
                            </td>
                            <td>
                                <a class="pl-3 pr-3" :href="'/maintenance/requests?status=new'" target="_blank">
                                    {{ stats.maintenance_requests.totalOpen }}
                                </a>
                            </td>
                            <td>
                                <a class="pl-3 pr-3" :href="'/maintenance/requests?status=closed'" target="_blank">
                                    {{ stats.maintenance_requests.totalClosed }}
                                </a>
                            </td>
                            <td>
                                <a class="pl-3 pr-3" :href="'/maintenance/requests?without_tickets=true'" target="_blank">
                                    {{ stats.maintenance_requests.totalWithoutTickets }}
                                </a>
                            </td>
                            <td>
                                <a class="pl-3 pr-3" :href="'/maintenance/requests?status=new&without_tickets=true'" target="_blank">
                                    {{ stats.maintenance_requests.totalOpenWithoutTickets }}
                                </a>
                            </td>
                            <td>
                                <a class="pl-3 pr-3" :href="'/maintenance/requests?status=closed&without_tickets=true'" target="_blank">
                                    {{ stats.maintenance_requests.totalClosedWithoutTickets }}
                                </a>
                            </td>
                        </tr>
                    </tbody>
                </table>
            </div>
        </div>

        <!-- User Inspections Table -->
        <div class="card mt-12 mb-12 reporting-db-section">
            <div class="card">
                <div class="card-body">
                    <h2 class="text-left"
                        style="font-weight: 700 !important; font-size: 2.2em !important">User Inspections Stats</h2>
                    <h6 class="card-subtitle mt-1">This table shows the inspection statistics for each user with inspections assigned</h6>
                </div>
                <div class="table-responsive">
                    <table class="table table-hover no-wrap">
                        <thead>
                            <tr>
                                <th>USER</th>
                                <th class="text-center">SCHEDULED <span style="font-size: 10px">(upcoming)</span></th>
                                <th class="text-center">OVERDUE</th>
                                <th class="text-center">UPLOADED <span style="font-size: 10px">(last 30 days)</span></th>
                            </tr>
                        </thead>
                        <tbody>

                            <tr class="reporting-db-normalize-row" v-for="user in stats.user_inspections">
                                <td>
                                    <a :href="'/inspections?selected_inspector='+user.user_id" target="_blank">
                                        {{ user.user_name }}
                                    </a>
                                </td>
                                <td class="text-center">
                                    <a class="pl-3 pr-3"
                                       :href="'/inspections?status_filter=scheduled&selected_inspector=' + user.user_id"
                                       target="_blank">
                                        {{ user.scheduled }}
                                    </a>
                                </td>
                                <td class="text-center">
                                    <a class="pl-3 pr-3"
                                       :href="'/inspections?status_filter=scheduled&overdue_filter=true&selected_inspector=' + user.user_id "
                                       target="_blank">
                                        {{ user.overdue }}
                                    </a>
                                </td>
                                <td class="text-center">
                                    {{ user.uploaded_last_30_days }}
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            </div>
        </div>

        <!-- Most Used Service Providers -->
        <div class="card mt-12 mb-12 reporting-db-section">
            <div class="card">
                <div class="card-body">
                    <h2 class="text-left"
                        style="font-weight: 700 !important; font-size: 2.2em !important">Most used service providers - Last 6 months</h2>
                    <h6 class="card-subtitle mt-1">This table shows the details of the top 20 most used service providers during the last 6 months</h6>
                </div>
                <div class="table-responsive">
                    <table class="table table-hover no-wrap">
                        <thead>
                            <tr>
                                <th>SERVICE PROVIDER</th>
                                <th class="text-center">NR OF TICKETS ASSIGNED</th>
                                <th class="text-right">VALUE OF ASSIGNED TICKETS</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr class="reporting-db-normalize-row" v-for="sp in stats.most_used_service_providers_6_months">
                                <td>
                                    <a :href="'/serviceproviders/' + sp.id" target="_blank">
                                        {{ sp.name }}
                                    </a>
                                </td>
                                <td class="text-center"> {{ sp.nr_of_tickets_last_6_months }}</td>
                                <td class="text-right"> {{ sp.ticket_value_last_6_months }}</td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            </div>
        </div>

        <!-- Asset Stats -->
        <div class="card mt-12 mb-12 reporting-db-section">
            <asset-stats></asset-stats>
        </div>

    </div>
</template>

<script>
export default {
    name: "ReportingDashboard",

    data () {
        return {
            ticketMovementOptions: {
                chart: {
                    toolbar: {
                        show: false
                    },
                    zoom: {
                        enabled: false,
                    }
                },
                stroke: {
                    curve: "straight"
                },
                plotOptions: {
                    bar: {
                        dataLabels: {
                            position: "top", // top, center, bottom
                        },
                    }
                },
                dataLabels: {
                    enabled: false,
                    formatter: function (val) {
                        /*return val + "%";*/
                        return Number.parseFloat(val).toFixed(0);
                    },
                    offsetY: -20,
                    style: {
                        fontSize: "12px",
                        colors: ["#132a37", "#132a37"]
                    }
                },
                xaxis: {
                    categories: undefined,
                    position: "bottom",
                    crosshairs: {
                        fill: {
                            type: "gradient",
                            gradient: {
                                colorFrom: "#132a37",
                                colorTo: "#1a445b",
                                stops: [-10000, 10000],
                                opacityFrom: 0.4,
                                opacityTo: 0.5,
                            }
                        }
                    },
                    tooltip: {
                        enabled: true,
                    }
                },
                yaxis: {
                    labels: {
                        show: true,
                        formatter: function (val) {
                            return Number.parseFloat(val).toFixed(0);
                        }
                    }

                },
            },
            ticketMovementSeries: [
                {
                    name: "Tickets Opened",
                    data: []
                },
                {
                    name: "Tickets Closed",
                    data: []
                },
                {
                    name: "Currently Open",
                    data: []
                },
                {
                    name: "Currently Unresolved",
                    data: []
                }
            ],
            ticketPriorityOptions: {
                legend: {
                    position: "top",
                    offsetY: -4
                },
                plotOptions: {
                    pie: {
                        customScale: 1.2,
                        donut: {
                            size: "40%"
                        }
                    }
                },
                labels: ["Low", "Medium", "High"],
                colors: ["rgba(41, 206, 125, 0.8)", "rgba(64, 158, 255, 0.62)", "rgba(245, 37, 29, 0.6)"]
            },
            ticketPrioritySeries: [],
            stats: {
                inspections: {},
                tickets: {},
                settings: {},
                maintenance_requests: {},
            }
        };
    },

    mounted () {
        this.fetchStats();
    },

    methods: {
        fetchStats () {
            axios.get("/reporting/")
            .then(response => {
                this.stats = response.data.stats;
                this.ticketMovementSeries = response.data.ticket_movement_stats_chart.series;
                Vue.set(this.ticketMovementOptions.xaxis, "categories", response.data.ticket_movement_stats_chart.labels);

                this.ticketPrioritySeries = response.data.ticket_priority_distribution;
            }).catch(error => {
                toastr.error(error.message);
            });
        },
    }

};
</script>

<style scoped>

</style>
